import React, { useState, useEffect, useContext, useMemo } from "react";
import { LocalesProvider, LocalesContext } from "./contexts/LocalesContext";
import SectionContentComponent from "./components/SectionContentComponent";
import SectionContentBlockComponent from "./components/SectionContentBlockComponent";
import CardComponent from "./components/CardComponent";
import HistoricalComponent from "./components/HistoricalComponent";

import {
  secondsToPrettyTime,
  timeToPrettyString,
} from "./utils/data";

function SessionAlarms(props) {
  const { session } = props;
  const unitsSystem = props.units_system;
  const sessionType = props.session_type || 'irriapp';
  const {
    t,
    translations,
    actions: localesActions,
  } = useContext(LocalesContext);

  // Stato per miplus2
  const [miplus2Session, setMiplus2Session] = useState(null);

  // MEMOS ******************************************************************

  const alarmData = useMemo(() => {
    const data = [];
    props.session_snapshots.filter((s) => {
      if (s.typology === "alarm") {
        const snapshotTimestamp = new Date(s.timestamp);
        const snapshotValue = s.value_elaborated;
        data.push({
          date: timeToPrettyString(snapshotTimestamp),
          value: snapshotValue,
          // Solo MiPlus ha lifetime negli allarmi
          subvalue: sessionType === 'miplus' && s.lifetime && s.lifetime > 60 ? `x ${secondsToPrettyTime(s.lifetime)}` : null 
        });
      }
    });

    return data;
  }, [translations, props.session_snapshots, sessionType]);

  const cmdData = useMemo(() => {
    // Solo IrriApp ha cmdData
    if (sessionType !== 'irriapp') return [];
    
    const data = [];
    props.session_snapshots.filter((s) => {
      if (s.typology === "cmd") {
        const snapshotTimestamp = new Date(s.timestamp);
        const snapshotValue = s.value_elaborated;
        data.push({
          date: timeToPrettyString(snapshotTimestamp),
          value: snapshotValue,
        });
      }
    });

    return data;
  }, [props.session_snapshots, sessionType]);

  // Dati allarmi per raindancer
  const raindancerAlarmData = useMemo(() => {
    if (sessionType !== 'raindancer' || !session.params) return [];
    
    const data = [];
    
    // Controlla se esistono allarmi nei params
    if (session.params.alarms && Array.isArray(session.params.alarms)) {
      session.params.alarms.forEach((alarm) => {
        const alarmTimestamp = new Date(alarm.timestamp);
        data.push({
          date: timeToPrettyString(alarmTimestamp),
          value: `Error Code: ${alarm.error}`,
        });
      });
    }
    
    return data.reverse(); // Mostra i più recenti per primi
  }, [session, sessionType]);

  // Dati allarmi per miplus2
  const alarmsInData = useMemo(() => {
    if (sessionType !== 'miplus2' || !miplus2Session) return [];
    
    const data = [];
    
    // Controllo se esistono logs e serials
    if (!miplus2Session.logs || !miplus2Session.serials || !miplus2Session.serials[0]) {
      console.log('Miplus2 logs o serials non trovati');
      return [];
    }

    const logs = miplus2Session.logs[miplus2Session.serials[0]];
    if (!logs) {
      console.log('Log per serial non trovati');
      return [];
    }

    console.log(`Elaborando ${logs.length} log per alarms in`);

    logs.forEach((log) => {
      if (log.event === 'work_alarm_enter') {
        const alarmTimestamp = new Date(log.timestamp * 1000);
        
        // Traduci direttamente il codice allarme
        const categoryTitle = t(`miplus2.alarms.${log.params.code}.title`);
        const alarmName = t(`miplus2.alarms.${log.params.code}.${log.params.subcode}`);
        
        // Controlla se esistono le traduzioni
        const hasTranslation = !categoryTitle.includes('missing') && !alarmName.includes('missing');
        
        const alarmDetails = hasTranslation 
          ? `${log.params.code}.${log.params.subcode} | ${categoryTitle} - ${alarmName} | PARAMS ${log.params.params.map((p) => p.toString(16))}`
          : `CODE ${log.params.code}.${log.params.subcode} | PARAMS ${log.params.params.map((p) => p.toString(16))}`; // Fallback al formato originale
        
        data.push({
          date: timeToPrettyString(alarmTimestamp),
          value: alarmDetails,
        });
      }
    });

    console.log(`Trovati ${data.length} alarms in`);
    return data.reverse(); // Mostra i più recenti per primi
  }, [miplus2Session, sessionType, props.session]);

  const alarmsOutData = useMemo(() => {
    if (sessionType !== 'miplus2' || !miplus2Session) return [];
    
    const data = [];
    
    // Controllo se esistono logs e serials
    if (!miplus2Session.logs || !miplus2Session.serials || !miplus2Session.serials[0]) {
      return [];
    }

    const logs = miplus2Session.logs[miplus2Session.serials[0]];
    if (!logs) {
      return [];
    }

    console.log(`Elaborando ${logs.length} log per alarms out`);

    logs.forEach((log) => {
      if (log.event === 'work_alarm_exit') {
        const alarmTimestamp = new Date(log.timestamp * 1000);
        
        // Traduci direttamente il codice allarme
        const categoryTitle = t(`miplus2.alarms.${log.params.code}.title`);
        const alarmName = t(`miplus2.alarms.${log.params.code}.${log.params.subcode}`);
        
        // Controlla se esistono le traduzioni
        const hasTranslation = !categoryTitle.includes('missing') && !alarmName.includes('missing');
        
        const alarmDetails = hasTranslation 
          ? `${log.params.code}.${log.params.subcode} | ${categoryTitle} - ${alarmName} | PARAMS ${log.params.params.map((p) => p.toString(16))}`
          : `CODE ${log.params.code}.${log.params.subcode} | PARAMS ${log.params.params.map((p) => p.toString(16))}`; // Fallback al formato originale
        
        data.push({
          date: timeToPrettyString(alarmTimestamp),
          value: alarmDetails,
        });
      }
    });

    console.log(`Trovati ${data.length} alarms out`);
    return data.reverse(); // Mostra i più recenti per primi
  }, [miplus2Session, sessionType, props.session]);

  // EFFECTS ******************************************************************

  useEffect(() => {
    localesActions.setLocale(props.locale);
    localesActions.setTranslations(props.translations);
  }, []);

  useEffect(() => {
    
    if (sessionType === 'miplus2' && props.data_json_url) {
      console.log('Caricamento dati miplus2...');
      fetch(props.data_json_url)
        .then(response => response.json())
        .then(data => {
          console.log('Dati miplus2 caricati:', data);
          setMiplus2Session(data);
        })
        .catch(error => {
          console.error('Errore nel caricamento dati miplus2:', error);
        });
    }
  }, [sessionType, props.data_json_url]);

  // RENDER ******************************************************************

  return (
    <SectionContentComponent>
      {sessionType === 'irriapp' ? (
        <div className={"row row--ais"}>
          {/* IrriApp ha sia comandi che allarmi */}
          <div className={"col-45"}>
            <HistoricalComponent data={cmdData} title={t("application.titles.session_command_history")} />
          </div>
          <div className={"col-45"}>
              <HistoricalComponent data={alarmData} title={t("application.titles.session_alarm_history")} />
          </div>
        </div>
      ) : sessionType === 'miplus2' ? (
        <div className={"row row--ais"}>
          {/* MiPlus2 ha alarms in e alarms out */}
          <div className={"col-45"}>
            <HistoricalComponent data={alarmsInData} title="Alarms In" />
          </div>
          <div className={"col-45"}>
            <HistoricalComponent data={alarmsOutData} title="Alarms Out" />
          </div>
        </div>
      ) : sessionType === 'raindancer' ? (
        <div className={"row"}>
          {/* Raindancer ha solo allarmi */}
          <div className={"col-6"}>
            <HistoricalComponent data={raindancerAlarmData} title={t("application.titles.session_alarm_history")} />
          </div>
        </div>
      ) : (
        <div className={"row"}>
          {/* MiPlus ha solo allarmi */}
            <HistoricalComponent data={alarmData} title={t("application.titles.session_alarm_history")} />
        </div>
      )}
    </SectionContentComponent>
  );
}

export default (props) => {
  return (
    <LocalesProvider>
      <SessionAlarms {...props} />
    </LocalesProvider>
  );
};